import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Wallet, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { API, AuthContext } from '../App';
import { CompanyContext, useCurrency, formatCurrency } from '../contexts/CompanyContext';
import RowActions from '../components/RowActions';
import { generateVoucherPDF } from '../lib/quotationPdf';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  pending_approval: 'bg-amber-100 text-amber-800 border border-amber-300',
  submitted: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800 border border-rose-300',
  paid: 'bg-indigo-100 text-indigo-800',
};

const blank = (voucher_type) => ({
  voucher_type,
  party_name: '',
  date: new Date().toISOString().slice(0, 10),
  amount: 0,
  currency: 'AED',
  mode: 'cash',
  cheque_number: '',
  bank_name: '',
  reference_number: '',
  narration: '',
  against_invoice_id: '',
  against_lpo_id: '',
});

export default function Vouchers() {
  const { user } = useContext(AuthContext);
  const { company } = useContext(CompanyContext);
  const { symbol } = useCurrency();

  const [voucherType, setVoucherType] = useState('payment');
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [draft, setDraft] = useState(blank('payment'));
  const [editing, setEditing] = useState(null);

  const canApprove = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'].includes(user?.role);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { voucher_type: voucherType };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await axios.get(`${API}/vouchers`, { params, withCredentials: true });
      setItems(r.data || []);
    } catch (e) {
      if (e?.response?.status === 402 || e?.response?.status === 403) {
        toast.error('Vouchers require the Enterprise plan.');
      } else {
        toast.error('Failed to load vouchers');
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [voucherType, statusFilter, dateFrom, dateTo]);

  const openNew = () => { setEditing(null); setDraft(blank(voucherType)); setShowDialog(true); };
  const openEdit = (v) => { setEditing(v.id); setDraft({ ...v, date: v.date?.slice(0, 10) }); setShowDialog(true); };

  const save = async () => {
    if (!draft.party_name) { toast.error('Party is required'); return; }
    const payload = {
      ...draft,
      amount: parseFloat(draft.amount) || 0,
      date: draft.date ? new Date(draft.date).toISOString() : null,
    };
    try {
      if (editing) await axios.put(`${API}/vouchers/${editing}`, payload, { withCredentials: true });
      else await axios.post(`${API}/vouchers`, payload, { withCredentials: true });
      toast.success('Saved'); setShowDialog(false); fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const approve = async (id) => { await axios.post(`${API}/vouchers/${id}/approve`, {}, { withCredentials: true }); toast.success('Approved'); fetchData(); };
  const submitForApproval = async (id) => {
    try {
      await axios.post(`${API}/vouchers/${id}/submit-for-approval`, {}, { withCredentials: true });
      toast.success('Sent for approval');
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to submit'); }
  };
  const reject = async (id) => {
    const reason = window.prompt('Reason for rejection?', '');
    if (reason === null) return;
    try {
      await axios.post(`${API}/vouchers/${id}/reject`, { reason }, { withCredentials: true });
      toast.success('Rejected');
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to reject'); }
  };
  const duplicate = async (id) => { await axios.post(`${API}/vouchers/${id}/duplicate`, {}, { withCredentials: true }); toast.success('Duplicated'); fetchData(); };
  const remove = async (id) => { await axios.delete(`${API}/vouchers/${id}`, { withCredentials: true }); toast.success('Deleted'); fetchData(); };

  return (
    <Layout>
      <div data-testid="vouchers-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <Wallet className="w-6 h-6 text-emerald-600" />Vouchers
            </h1>
            <p className="text-sm text-muted-foreground">Payment vouchers (cash out) and receipt vouchers (cash in) — letter-headed PDFs with approval stamps.</p>
          </div>
          <Button data-testid="new-voucher-btn" onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-1" />New Voucher</Button>
        </div>

        <div className="flex flex-wrap gap-3 items-center bg-white border border-slate-200 rounded-lg p-3">
          <div className="flex gap-1 p-0.5 rounded-md bg-slate-100">
            {[{ k: 'payment', label: 'Payment' }, { k: 'receipt', label: 'Receipt' }].map(opt => (
              <button key={opt.k} data-testid={`voucher-type-${opt.k}`} onClick={() => setVoucherType(opt.k)}
                className={`px-3 py-1.5 rounded text-sm font-medium transition ${voucherType === opt.k ? 'bg-white text-emerald-700 shadow' : 'text-slate-600'}`}>{opt.label}</button>
            ))}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending_approval">Waiting for Approval</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" />
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading…</div> : (
          <div className="grid gap-3">
            {items.length === 0 && <Card className="p-12 text-center"><Wallet className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-muted-foreground">No vouchers yet</p></Card>}
            {items.map(v => (
              <Card key={v.id} data-testid={`voucher-card-${v.id}`} className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-mono text-slate-500">{v.voucher_no}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${STATUS_COLORS[v.status] || 'bg-slate-100'}`}>
                        {v.status === 'pending_approval' ? 'Waiting for Approval' : v.status}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-700 capitalize">{v.mode}</span>
                    </div>
                    <div className="font-semibold text-foreground truncate">{v.party_name}</div>
                    <div className="text-xs text-muted-foreground">{v.narration?.slice(0, 100) || '—'} · {v.date?.split('T')[0]}</div>
                    {v.status === 'rejected' && v.rejected_reason && (
                      <div className="text-xs text-rose-700 mt-1">Rejected: {v.rejected_reason}</div>
                    )}
                    {v.status === 'approved' && v.approved_by_name && (
                      <div className="text-xs text-emerald-700 mt-1">Approved by {v.approved_by_name}{v.approved_at ? ` on ${new Date(v.approved_at).toLocaleDateString()}` : ''}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Amount</div>
                      <div className="text-lg font-bold text-emerald-700">{formatCurrency(v.amount || 0, symbol)}</div>
                    </div>
                    {v.status === 'draft' && (
                      <Button size="sm" variant="outline" onClick={() => submitForApproval(v.id)} data-testid={`voucher-submit-${v.id}`}>
                        Submit for Approval
                      </Button>
                    )}
                    {v.status === 'pending_approval' && canApprove && (
                      <>
                        <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-600" onClick={() => approve(v.id)} data-testid={`voucher-approve-${v.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve</Button>
                        <Button size="sm" variant="outline" className="text-rose-600 border-rose-300" onClick={() => reject(v.id)} data-testid={`voucher-reject-${v.id}`}>Reject</Button>
                      </>
                    )}
                    {v.status === 'draft' && canApprove && (
                      <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-600" onClick={() => approve(v.id)} data-testid={`voucher-approve-${v.id}`} title="Manager can approve directly from draft"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve</Button>
                    )}
                    <RowActions
                      testIdPrefix={`voucher-${v.id}`}
                      onEdit={v.status === 'draft' ? () => openEdit(v) : undefined}
                      onDuplicate={() => duplicate(v.id)}
                      onDownloadPdf={() => generateVoucherPDF(v, { company })}
                      onDelete={v.status === 'draft' ? () => remove(v.id) : undefined}
                      deleteLabel={`voucher ${v.voucher_no}`}
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} {voucherType === 'receipt' ? 'Receipt' : 'Payment'} Voucher</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>{voucherType === 'receipt' ? 'Received from' : 'Paid to'}</Label><Input data-testid="voucher-party-input" value={draft.party_name} onChange={(e) => setDraft({ ...draft, party_name: e.target.value })} /></div>
              <div><Label>Date</Label><Input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} /></div>
              <div><Label>Amount</Label><Input data-testid="voucher-amount-input" type="number" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} /></div>
              <div><Label>Mode</Label>
                <Select value={draft.mode} onValueChange={(v) => setDraft({ ...draft, mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="cheque">Cheque</SelectItem><SelectItem value="bank">Bank Transfer</SelectItem><SelectItem value="card">Card</SelectItem></SelectContent>
                </Select>
              </div>
              {draft.mode === 'cheque' && <>
                <div><Label>Cheque #</Label><Input value={draft.cheque_number} onChange={(e) => setDraft({ ...draft, cheque_number: e.target.value })} /></div>
                <div><Label>Bank</Label><Input value={draft.bank_name} onChange={(e) => setDraft({ ...draft, bank_name: e.target.value })} /></div>
              </>}
              <div><Label>Reference #</Label><Input value={draft.reference_number} onChange={(e) => setDraft({ ...draft, reference_number: e.target.value })} /></div>
              <div><Label>Against Invoice / LPO</Label><Input value={draft.against_invoice_id || draft.against_lpo_id} onChange={(e) => setDraft({ ...draft, against_invoice_id: e.target.value })} placeholder="ID" /></div>
              <div className="sm:col-span-2"><Label>Narration</Label><Textarea rows={3} value={draft.narration} onChange={(e) => setDraft({ ...draft, narration: e.target.value })} /></div>
            </div>
            <Button onClick={save} className="w-full bg-emerald-600 hover:bg-emerald-700 mt-3" data-testid="voucher-save-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
